/**
 * ConsumablesTracker.js
 * Tracks consumable items/materials used during surgery with cost tracking.
 * Supports product search, batch selection, stock validation, and removal.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import CircularProgress from '@material-ui/core/CircularProgress';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import TextField from '../../Utils/FormInputs/TextField';
import Label from '../../Utils/FormInputs/Label';
import { AutoCompleteUnControlled } from '../../Utils/FormInputs/AutoCompleteInput';
import { surgeryService } from '../../../services/surgeryService';

const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return 'USD 0';
  return `USD ${Number(amount).toLocaleString()}`;
};

const useStyles = makeStyles(() => ({
  root: {
    padding: '24px 28px',
    borderRadius: 8,
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#212121',
    marginBottom: 2,
  },
  subtitle: {
    fontSize: '0.82rem',
    color: '#757575',
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#9E9E9E',
    marginBottom: 14,
    paddingBottom: 8,
    borderBottom: '1px solid #F5F5F5',
  },
  // Toolbar
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  filterWrap: {
    flex: 1,
  },
  // Add form
  addForm: {
    padding: 16,
    marginBottom: 16,
    backgroundColor: '#FAFAFA',
    borderRadius: 6,
    border: '1px solid #F0F0F0',
  },
  addFormTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#424242',
    marginBottom: 12,
  },
  selectedProduct: {
    padding: '8px 12px',
    backgroundColor: '#E8F5E9',
    borderRadius: 4,
    fontSize: '0.82rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  batchOption: {
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '0.82rem',
    borderBottom: '1px solid #F5F5F5',
    transition: 'background-color 0.15s',
    '&:hover': { backgroundColor: '#E3F2FD' },
  },
  // Table
  tableContainer: {
    '& .MuiTableCell-root': { padding: '10px 12px', fontSize: '0.82rem' },
    '& .MuiTableCell-head': {
      fontWeight: 600, color: '#9E9E9E', backgroundColor: '#FAFAFA',
      fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em',
    },
  },
  costCell: {
    fontFamily: 'monospace',
    fontWeight: 500,
    textAlign: 'right',
  },
  emptyState: {
    textAlign: 'center',
    padding: '32px 16px',
    color: '#BDBDBD',
    fontStyle: 'italic',
    fontSize: '0.82rem',
  },
  itemCount: {
    fontSize: '0.78rem',
    color: '#9E9E9E',
    marginTop: 8,
  },
  // Total bar
  totalBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    backgroundColor: '#E8F5E9',
    borderRadius: 8,
    marginTop: 16,
    border: '1px solid #C8E6C9',
  },
  totalLabel: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#2E7D32',
  },
  totalAmount: {
    fontSize: '1.15rem',
    fontWeight: 700,
    color: '#1B5E20',
    fontFamily: 'monospace',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 120,
  },
}));

const ConsumablesTracker = ({ surgeryId }) => {
  const classes = useStyles();

  const [consumables, setConsumables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  // Product search
  const [productOptions, setProductOptions] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productKey, setProductKey] = useState(0);
  const productDebounceRef = useRef(null);

  // Batch
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [loadingBatches, setLoadingBatches] = useState(false);

  // Form
  const [quantity, setQuantity] = useState('');
  const [unitCost, setUnitCost] = useState('');

  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadConsumables = useCallback(async () => {
    setLoading(true);
    try {
      const response = await surgeryService.listConsumables(surgeryId);
      const items = response.data?.consumables || [];
      setConsumables(items.map((item) => ({
        ...item,
        id: item.consumable_id || item.id || Date.now() + Math.random(),
      })));
    } catch {
      setSnackbar({ open: true, message: 'Failed to load consumables', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [surgeryId]);

  useEffect(() => { loadConsumables(); }, [loadConsumables]);

  // ── Product search ────────────────────────────────
  const handleProductSearch = (inputValue) => {
    clearTimeout(productDebounceRef.current);
    if (!inputValue || inputValue.length < 2) { setProductOptions([]); return; }
    productDebounceRef.current = setTimeout(async () => {
      try {
        const res = await surgeryService.searchSurgeryProducts(inputValue);
        const data = res.data || [];
        setProductOptions(Array.isArray(data) ? data.filter((p) => typeof p === 'object') : []);
      } catch { setProductOptions([]); }
    }, 400);
  };

  const handleProductSelect = async (obj) => {
    const label = obj?.innerText || obj?.label || '';
    const value = obj?.value || '';
    const sell_price = obj?.sell_price || '';
    setSelectedProduct({ value, label, sell_price });
    setUnitCost(sell_price || '');
    setSelectedBatch(null);
    setBatches([]);

    if (!value) return;
    setLoadingBatches(true);
    try {
      const res = await surgeryService.getProductBatches(value);
      const batchData = res.data?.product || res.data || [];
      const batchList = Array.isArray(batchData) ? batchData.map((b) => ({
        batch_id: b.batch_id, expiry_date: b.expiry_date, quantity: b.quantity || 0,
      })) : [];
      setBatches(batchList);
      if (batchList.length === 1) setSelectedBatch(batchList[0]);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load batches', severity: 'error' });
    } finally {
      setLoadingBatches(false);
    }
  };

  const resetForm = () => {
    setSelectedProduct(null);
    setSelectedBatch(null);
    setBatches([]);
    setQuantity('');
    setUnitCost('');
    setProductOptions([]);
    setProductKey((k) => k + 1);
  };

  const handleAddConsumable = async () => {
    if (!selectedProduct) {
      setSnackbar({ open: true, message: 'Select a product from surgery stock', severity: 'warning' });
      return;
    }
    if (!selectedBatch) {
      setSnackbar({ open: true, message: 'Select a batch', severity: 'warning' });
      return;
    }
    if (!quantity || Number(quantity) < 1) {
      setSnackbar({ open: true, message: 'Quantity must be at least 1', severity: 'warning' });
      return;
    }
    if (Number(quantity) > selectedBatch.quantity) {
      setSnackbar({ open: true, message: `Only ${selectedBatch.quantity} available in this batch`, severity: 'warning' });
      return;
    }

    const cost = unitCost ? Number(unitCost) : 0;
    setSaving(true);
    try {
      const res = await surgeryService.addConsumable({
        surgery_id: surgeryId,
        product_id: selectedProduct.value,
        batch_id: selectedBatch.batch_id,
        quantity: Number(quantity),
        unit_cost: cost,
        name: selectedProduct.label,
      });
      setConsumables((prev) => [...prev, {
        id: res.data?.consumable_id || Date.now(),
        consumable_id: res.data?.consumable_id,
        name: selectedProduct.label,
        product_id: selectedProduct.value,
        batch_id: selectedBatch.batch_id,
        quantity: Number(quantity),
        unit_cost: cost,
        total: cost * Number(quantity),
      }]);
      resetForm();
      setShowAddForm(false);
      setSnackbar({ open: true, message: 'Consumable added & stock deducted', severity: 'success' });
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to add consumable';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveConsumable = async (item) => {
    const itemId = item.consumable_id || item.id;
    if (!item.consumable_id) {
      setConsumables((prev) => prev.filter((c) => c.id !== itemId));
      return;
    }
    setRemoving(itemId);
    try {
      await surgeryService.deleteConsumable({ consumable_id: item.consumable_id });
      setConsumables((prev) => prev.filter((c) => c.id !== item.id && c.consumable_id !== item.consumable_id));
      setSnackbar({ open: true, message: 'Consumable removed & stock restored', severity: 'success' });
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to remove consumable';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setRemoving(null);
    }
  };

  const filteredConsumables = searchTerm.trim()
    ? consumables.filter((c) => c.name?.toLowerCase().includes(searchTerm.toLowerCase()))
    : consumables;

  const runningTotal = consumables.reduce((sum, item) => sum + (item.total || (item.unit_cost * item.quantity) || 0), 0);
  const totalItems = consumables.reduce((sum, item) => sum + (item.quantity || 0), 0);

  if (loading) {
    return (
      <Paper className={classes.root} elevation={1}>
        <div className={classes.loadingContainer}><CircularProgress /></div>
      </Paper>
    );
  }

  return (
    <Paper className={classes.root} elevation={1}>
      <div className={classes.header}>
        <Typography className={classes.title}>Consumables Tracker</Typography>
        <Typography className={classes.subtitle}>
          Surgery #{surgeryId} -- Track items and materials used
        </Typography>
      </div>

      {/* Toolbar: filter + add button */}
      <div className={classes.toolbar}>
        <div className={classes.filterWrap}>
          <TextField
            name="search_consumables"
            placeholder="Filter recorded consumables..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            type="text"
          />
        </div>
        <button
          type="button"
          className="btn btn-sm sms-info-btn"
          onClick={() => setShowAddForm(true)}
          disabled={showAddForm}
        >
          + Add Item
        </button>
      </div>

      {/* Add form */}
      {showAddForm && (
        <div className={classes.addForm}>
          <Typography className={classes.addFormTitle}>Add Consumable from Surgery Stock</Typography>

          {!selectedProduct ? (
            <Form.Group>
              <Label name="Search Product" />
              <AutoCompleteUnControlled
                key={productKey}
                options={productOptions}
                idx="consumable"
                handleChange={(v) => handleProductSearch(v)}
                handleProductData={(obj) => handleProductSelect(obj)}
                placeholder="Type product name (min 2 chars)..."
              />
            </Form.Group>
          ) : (
            <div className={classes.selectedProduct}>
              <span><strong>{selectedProduct.label}</strong></span>
              <Button size="small" onClick={() => { setSelectedProduct(null); setSelectedBatch(null); setBatches([]); setProductKey((k) => k + 1); }}>
                Change
              </Button>
            </div>
          )}

          {/* Batch selection */}
          {selectedProduct && (
            <>
              {loadingBatches ? (
                <div style={{ padding: 8 }}><CircularProgress size={20} /> Loading batches...</div>
              ) : batches.length === 0 ? (
                <Typography variant="body2" color="error" style={{ marginBottom: 8, fontSize: '0.82rem' }}>
                  No batches with available stock in surgery store for this product.
                </Typography>
              ) : batches.length > 1 ? (
                <div style={{ marginBottom: 12 }}>
                  <Label name="Select Batch" />
                  <div style={{ border: '1px solid #E0E0E0', borderRadius: 4, maxHeight: 150, overflowY: 'auto' }}>
                    {batches.map((b) => (
                      <div
                        key={b.batch_id}
                        className={classes.batchOption}
                        style={{ backgroundColor: selectedBatch?.batch_id === b.batch_id ? '#E3F2FD' : 'transparent' }}
                        onClick={() => setSelectedBatch(b)}
                      >
                        <strong>Batch #{b.batch_id}</strong>
                        <span style={{ marginLeft: 12 }}>Stock: {b.quantity}</span>
                        {b.expiry_date && (
                          <span style={{ marginLeft: 12, color: '#999' }}>
                            Exp: {new Date(b.expiry_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <Typography variant="body2" style={{ marginBottom: 8, color: '#616161', fontSize: '0.82rem' }}>
                  Batch #{selectedBatch?.batch_id} -- Available: {selectedBatch?.quantity}
                  {selectedBatch?.expiry_date && ` -- Exp: ${new Date(selectedBatch.expiry_date).toLocaleDateString()}`}
                </Typography>
              )}
            </>
          )}

          {/* Quantity and cost fields */}
          {selectedProduct && selectedBatch && (
            <Form.Group as={Row} style={{ marginTop: 8 }}>
              <Col xs={6} sm={3}>
                <Label name="Quantity" />
                <TextField
                  name="consumable_qty"
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </Col>
              <Col xs={6} sm={3}>
                <Label name="Unit Cost (USD)" />
                <TextField
                  name="consumable_cost"
                  type="number"
                  value={unitCost}
                  onChange={(e) => setUnitCost(e.target.value)}
                />
              </Col>
              <Col xs={6} sm={3}>
                <Label name="Total" />
                <Typography variant="body2" style={{ marginTop: 6, fontWeight: 600, fontSize: '0.82rem' }}>
                  {formatCurrency(Number(unitCost || 0) * Number(quantity || 0))}
                </Typography>
              </Col>
              <Col xs={6} sm={3} style={{ display: 'flex', alignItems: 'flex-end', gap: 8, paddingBottom: 4 }}>
                <button
                  type="button"
                  className="btn btn-sm sms-btn"
                  onClick={handleAddConsumable}
                  disabled={saving}
                >
                  {saving ? 'Adding...' : 'Add'}
                </button>
                <button
                  type="button"
                  className="btn btn-sm sms-gray-btn"
                  onClick={() => { resetForm(); setShowAddForm(false); }}
                  disabled={saving}
                >
                  Cancel
                </button>
              </Col>
            </Form.Group>
          )}

          {!selectedProduct && (
            <button
              type="button"
              className="btn btn-sm sms-gray-btn mt-2"
              onClick={() => { resetForm(); setShowAddForm(false); }}
            >
              Cancel
            </button>
          )}
        </div>
      )}

      {/* Consumables table */}
      {consumables.length === 0 ? (
        <div className={classes.emptyState}>
          No consumables recorded yet. Click "Add Item" to begin tracking.
        </div>
      ) : (
        <>
          {searchTerm.trim() && filteredConsumables.length === 0 && (
            <Typography style={{ padding: '8px 0', color: '#BDBDBD', fontSize: '0.82rem', fontStyle: 'italic' }}>
              No items match "{searchTerm}"
            </Typography>
          )}
          <TableContainer className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Item Name</TableCell>
                  <TableCell align="center">Qty</TableCell>
                  <TableCell align="right">Unit Cost</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell style={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredConsumables.map((item, index) => (
                  <TableRow key={item.id || item.consumable_id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <span style={{ fontWeight: 500 }}>{item.name}</span>
                    </TableCell>
                    <TableCell align="center">{item.quantity}</TableCell>
                    <TableCell className={classes.costCell}>
                      {formatCurrency(item.unit_cost || item.unit_price)}
                    </TableCell>
                    <TableCell className={classes.costCell}>
                      {formatCurrency(item.total || item.total_price)}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveConsumable(item)}
                        disabled={removing === (item.consumable_id || item.id)}
                      >
                        {removing === (item.consumable_id || item.id) ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography className={classes.itemCount}>
            {consumables.length} item{consumables.length !== 1 ? 's' : ''} ({totalItems} units total)
          </Typography>
        </>
      )}

      {/* Running total */}
      <div className={classes.totalBar}>
        <Typography className={classes.totalLabel}>Running Total</Typography>
        <Typography className={classes.totalAmount}>{formatCurrency(runningTotal)}</Typography>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ConsumablesTracker;
